import { List, Typography, Tooltip } from 'antd';
import { styled } from 'styled-components';

const { Text, Title } = Typography;

export interface Meeting {
  id: string;
  title: string;
  date: string;
  participants: Array<{
    name: string;
    avatarUrl: string;
    companyLogoUrl?: string;
  }>;
  tags: Array<{
    label: string;
    color: string;
    aiGenerated?: boolean;
  }>;
}

interface CalendarListProps {
  timelines: Record<
    string,
    {
      timelineId: number;
      title: string;
      summaryAvailable: boolean;
      signalAwareRecap: string;
    }[]
  >;
  selectedTimeline: number | null;
  expandedMonth: string;
  onTimelineClick: (
    meeting: { timelineId: number; title: string; summaryAvailable: boolean },
    month: string,
  ) => void;
  onMonthClick: (month: string) => void;
}

const TimelineWrapper = styled.div`
  position: relative;
  /* height: calc(100vh - 64px); */
  /* overflow: auto; */
  /* padding-left: var(--spacing-03); */

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: var(--gray-10);
  }
`;

const MonthTitle = styled(Title)<{ $isExpanded: boolean }>`
  &.ant-typography {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    padding: 2px 0px 2px var(--spacing-03);
    margin-bottom: 0px;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-01);
    letter-spacing: 0px;
    cursor: pointer;
    color: ${(props) =>
      props.$isExpanded ? 'var(--gray-100)' : 'var(--gray-60)'};
    position: relative;
    font-weight: ${(props) =>
      props.$isExpanded
        ? 'var(--font-weight-bold)'
        : 'var(--font-weight-regular)'};
    transition: color 0.2s ease;
    &:hover {
      color: var(--gray-100);
    }

    ${(props) =>
      props.$isExpanded &&
      `
      &::after {
        content: '';
        position: absolute;
        left: 0px;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: var(--button-primary);
      }
    `}
  }
`;

const ListItem = styled(Text)<{ $isSelected?: boolean }>`
  &.ant-typography {
    display: block;
    color: ${(props) =>
      props.$isSelected ? 'var(--gray-100)' : 'var(--gray-60)'};
    cursor: pointer;
    position: relative;
    padding: 2px 0px 2px var(--spacing-05);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-01);
    letter-spacing: 0px;
    font-weight: ${(props) =>
      props.$isSelected
        ? 'var(--font-weight-bold)'
        : 'var(--font-weight-regular)'};
    transition: color 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 248px;

    &:hover {
      color: var(--gray-100);
    }

    ${(props) =>
      props.$isSelected &&
      `
      &::after {
        content: '';
        position: absolute;
        left: 0px;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: var(--button-primary);
      }
    `}
  }
`;

export const CalendarList = ({
  timelines,
  selectedTimeline,
  expandedMonth,
  onTimelineClick,
  onMonthClick,
}: CalendarListProps) => {
  return (
    <TimelineWrapper>
      {Object.entries(timelines).map(([monthYear, monthTimelines]) => (
        <div key={monthYear}>
          <MonthTitle
            level={2}
            onClick={() => onMonthClick(monthYear)}
            $isExpanded={expandedMonth === monthYear}
          >
            {monthYear}
          </MonthTitle>

          {expandedMonth === monthYear && monthTimelines?.length > 0 && (
            <div>
              <List
                dataSource={monthTimelines}
                renderItem={(meeting) => {
                  if (meeting?.signalAwareRecap || meeting?.summaryAvailable) {
                    return (
                      <Tooltip
                        key={meeting.timelineId}
                        title={meeting.title}
                        placement="right"
                        mouseEnterDelay={0.5}
                      >
                        <ListItem
                          $isSelected={selectedTimeline === meeting.timelineId}
                          onClick={() => onTimelineClick(meeting, monthYear)}
                        >
                          {meeting.title}
                        </ListItem>
                      </Tooltip>
                    );
                  }

                  return null;
                }}
              />
            </div>
          )}
        </div>
      ))}
    </TimelineWrapper>
  );
};
